import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useSheetAnim from '../hooks/useSheetAnim';
import { Chip, EmptyState, InfiniteSentinel, Skeletons, RM, listingPriceLabel, LISTING_TYPE_TONE, LISTING_STATUS_TONE } from './kit';
import * as listingsApi from '../api/listings';

/**
 * ListingPickerSheet — "link this to a listing", done properly.
 *
 * Linking a sale or a booking form to a property is the one pick in this app that a
 * one-line dropdown row genuinely cannot serve: an agent recognises a property by
 * its photo, its price and its area, not by a truncated "REF-0012 · 3-sty Terrace…"
 * string. So this is a full-screen sheet that browses the same transactable
 * inventory the Listings tab shows, with the same card, the same chips and the same
 * price rules — searchable, paged, and tappable to select.
 *
 * It OPENS ON A FEED rather than an empty search box: the caller always passes a
 * listingType, which is a structured filter the search endpoint accepts on its own,
 * so page 1 is the newest matching inventory. Typing narrows it server-side.
 *
 * Props:
 *   open, onClose
 *   listingType  — 'SUBSALE' | 'RENT' | 'AUCTION' | 'PROJECT' | 'SUBSALE,PROJECT'
 *   status       — narrows the transactable feed. Both callers now pass 'ACTIVE':
 *                  a property already UNDER_OFFER can take neither a second sale
 *                  nor a second booking form (the servers reject it, and approving
 *                  a second form was never possible). Omitted, the feed would still
 *                  be ACTIVE + UNDER_OFFER — which is why the UNDER_OFFER badge and
 *                  competing-offer amount below are currently unreachable. They are
 *                  kept, not deleted: the block is a policy call that may be
 *                  revisited for backup offers, and this is the UI it would need.
 *   linkable     — narrow the feed to inventory this agent can actually write
 *                  against: their own listings plus HQ stock. Another agent's
 *                  listing is left out because both servers refuse a sale or a
 *                  booking form raised on one — that goes through a co-share request
 *                  instead. Pass it from every LINK field; leave it off for browsing.
 *   value        — currently linked listing id (highlighted, shown as ✓ Linked)
 *   onSelect(listing) — the full row, so the caller can prefill address/price
 *   onClear      — when given, offers "Unlink" in the footer
 *   title, sub
 */

const Z = 4900;
const PAGE = 12;

/**
 * Who listed this property, in words. A bare "CID HQ" chip is a riddle — worse,
 * it sits a line above "Owner: …", which is a different person entirely (the
 * vendor). So the card states the relationship instead of labelling it:
 *   your own listing        → Your listing
 *   another agent's         → Listed by Ali Lee — another CID agent
 *   HQ-keyed / project stock→ Listed by CID HQ — head office, no agent owner
 * Returns null when the row carries no ownership fields (an older payload), so
 * the line disappears rather than guessing.
 */
export const listedBy = (l) => {
  if (!l) return null;
  if (l.is_mine) return { lead: 'Your listing', tail: null, mine: true };
  if (l.is_hq_listing) return { lead: 'Listed by CID HQ', tail: 'head office, no agent owner', mine: false };
  if (l.agent_name) return { lead: `Listed by ${l.agent_name}`, tail: 'another CID agent', mine: false };
  return null;
};

function ListedByLine({ listing, style }) {
  const by = listedBy(listing);
  if (!by) return null;
  return (
    <div style={{ fontSize: 11.5, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...style }}>
      <span style={{ fontWeight: 600, color: by.mine ? 'var(--ok)' : 'var(--text)' }}>{by.lead}</span>
      {by.tail ? ` — ${by.tail}` : ''}
    </div>
  );
}

export default function ListingPickerSheet({
  open, onClose, listingType, status, value, onSelect, onClear, linkable,
  title = 'Link a listing', sub = 'tap a property to link it',
}) {
  const { mounted, closing } = useSheetAnim(open);
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);
  const debounce = useRef(null);
  // Every fetch carries a generation; a stale response (slow page 1 landing after a
  // newer search) is dropped rather than appended to the wrong list.
  const gen = useRef(0);
  // The sentinel's observer can fire twice before a setState lands, which would
  // append the same page twice (duplicate React keys). A ref flips synchronously.
  const busy = useRef(false);

  // Reset to a clean feed each time the sheet opens — a stale search term from last
  // time reads as "no listings exist".
  useEffect(() => {
    if (!open) return;
    setQ(''); setTerm(''); setItems([]); setPage(1); setLoading(true); setError(null);
  }, [open]);

  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => setTerm(q.trim()), 320);
    return () => clearTimeout(debounce.current);
  }, [q]);

  const fetchPage = useCallback(async (p, replace) => {
    if (busy.current && !replace) return;
    busy.current = true;
    const g = ++gen.current;
    if (replace) setLoading(true); else setLoadingMore(true);
    setError(null);
    try {
      const r = await listingsApi.searchListings({
        q: term || undefined,
        listing_type: listingType || undefined,
        status: status || undefined,
        linkable: linkable ? 1 : undefined,
        page: p,
        limit: PAGE,
      });
      if (g !== gen.current) return;
      const rows = (r?.data ?? r) || [];
      setItems((prev) => (replace ? rows : [...prev, ...rows]));
      setPage(p);
      // A short page is the end of the feed — the endpoint returns a bare array,
      // so there's no total to compare against.
      setHasMore(rows.length >= PAGE);
    } catch (e) {
      if (g !== gen.current) return;
      setError(e?.message || 'Could not load listings');
      if (replace) setItems([]);
      setHasMore(false);
    } finally {
      busy.current = false;
      if (g === gen.current) { setLoading(false); setLoadingMore(false); }
    }
  }, [term, listingType, status, linkable]);

  useEffect(() => {
    if (!open) return;
    fetchPage(1, true);
  }, [open, fetchPage]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!mounted) return null;

  const pick = (l) => { onSelect?.(l); onClose?.(); };

  return createPortal(
    <div
      className={`sheet-panel${closing ? ' is-closing' : ''}`}
      style={{ position: 'fixed', inset: 0, zIndex: Z, background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '10px 14px 10px', paddingTop: 'calc(10px + env(safe-area-inset-top))', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flex: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            onClick={onClose}
            role="button"
            tabIndex={0}
            aria-label="Close"
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClose?.(); } }}
            style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flex: 'none' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
            <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{sub}</div>
          </div>
          {onClear && value && (
            <span
              onClick={() => { onClear(); onClose?.(); }}
              style={{ flex: 'none', fontSize: 12.5, fontWeight: 600, color: 'var(--bad)', cursor: 'pointer', padding: '6px 4px' }}
            >
              Unlink
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: 'var(--field-h)', padding: '0 12px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 12 }}>
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="var(--muted)" strokeWidth="2" strokeLinecap="round" style={{ flex: 'none' }}>
            <circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="21" y2="21" />
          </svg>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search ref, title, address or area…"
            inputMode="search"
            style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontSize: 14, color: 'var(--text)' }}
          />
          {q && (
            <span onClick={() => setQ('')} style={{ flex: 'none', width: 18, height: 18, borderRadius: 50, background: 'var(--chip)', color: 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, cursor: 'pointer', lineHeight: 1 }}>✕</span>
          )}
        </div>
      </div>

      <div className="scrx" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '15px 16px', display: 'flex', flexDirection: 'column', gap: 12, WebkitOverflowScrolling: 'touch' }}>
        {loading ? (
          <Skeletons n={5} height={92} />
        ) : error ? (
          <EmptyState title="Couldn’t load listings" sub={error} />
        ) : items.length === 0 ? (
          <EmptyState
            title={term ? 'No listings match' : 'No listings available'}
            sub={term
              ? `Nothing in your transactable inventory matches “${term}”. Clear the search, or skip linking and fill the property details by hand.`
              : linkable
                ? 'You have no active listings of this type. Another agent’s listing cannot be linked here — open it from the Listings tab and send a co-share request instead.'
                : 'There are no active listings of this type yet — yours or other agents’. You can still fill the property details by hand.'}
          />
        ) : (
          <>
            {items.map((l) => (
              <ListingCard key={l.id} l={l} linked={l.id === value} onClick={() => pick(l)} />
            ))}
            <InfiniteSentinel hasMore={hasMore} loading={loadingMore} onReach={() => fetchPage(page + 1, false)} />
          </>
        )}
        <div style={{ height: 8 }} />
      </div>
    </div>,
    document.body
  );
}

// One label/value cell of the linked-listing spec grid. Renders nothing when the
// listing has no value for it, so the grid never shows a column of dashes.
function Spec({ label, value }) {
  if (value === null || value === undefined || value === '' ) return null;
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, color: 'var(--muted)', letterSpacing: '.02em' }}>{label}</div>
      <div style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>
    </div>
  );
}

const sqft = (n) => (n != null ? `${Number(n).toLocaleString('en-US')} sqft` : null);

/**
 * ListingLinkField — the form-side half of the picker: a field that opens the sheet
 * and, once something is linked, becomes a summary card of the linked property.
 *
 * The card carries the same facts the admin portal's linked-listing Descriptions
 * block does — ref, listing type, property type, tenure, built-up, land area,
 * bedrooms, bathrooms, floor, facing, asking price, address — but as a photo header
 * plus a two-column spec grid, because a desktop 3-column description list is
 * unreadable at 390px. Specs the listing doesn't carry are dropped rather than
 * printed as "-", so a bare land listing stays short.
 *
 * `listing` may be null even when `value` is set — an edit-mode form knows the id
 * before it has fetched the row — so the card degrades to just the id's label.
 */
export function ListingLinkField({
  value, listing, listingType, status, onChange, disabled, linkable,
  placeholder = 'Search & link a listing…',
  title = 'Link a listing', sub = 'tap a property to link it', pickedLabel,
}) {
  const [open, setOpen] = useState(false);
  const linked = !!value;
  // The search feed calls it `tenure`; the detail endpoint calls it
  // `property_tenure` — an edit-mode card is fed by the latter.
  const tenure = listing?.tenure || listing?.property_tenure || null;
  const hasSpecs = !!listing && [
    listing.property_type, tenure, listing.built_up_sqft, listing.land_area_sqft,
    listing.bedrooms, listing.bathrooms, listing.floor_level, listing.facing,
  ].some((v) => v !== null && v !== undefined && v !== '');

  return (
    <>
      {linked ? (
        <div style={{ background: 'var(--chip)', border: '1.5px solid var(--primary)', borderRadius: 14, padding: 11, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 11 }}>
            {listing?.cover_image_url ? (
              <img src={listing.cover_image_url} alt="" style={{ width: 58, height: 58, borderRadius: 10, flex: 'none', objectFit: 'cover' }} />
            ) : (
              <div style={{ width: 58, height: 58, borderRadius: 10, flex: 'none', background: 'repeating-linear-gradient(45deg,var(--surface2),var(--surface2) 6px,var(--surface) 6px,var(--surface) 12px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontFamily: 'ui-monospace,monospace', fontSize: 10.5, color: 'var(--muted)' }}>photo</span>
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {listing?.property_title || listing?.property_address || pickedLabel || 'Linked listing'}
              </div>
              {listing && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <span style={{ fontFamily: 'ui-monospace,monospace', fontSize: 11.5, color: 'var(--muted)' }}>{listing.listing_ref}</span>
                    {listing.listing_type && <Chip label={listing.listing_type} tone={LISTING_TYPE_TONE[listing.listing_type]} />}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)' }}>{listingPriceLabel(listing)}</div>
                  <ListedByLine listing={listing} />
                </>
              )}
            </div>
          </div>

          {hasSpecs && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px', paddingTop: 9, borderTop: '1px solid color-mix(in srgb,var(--primary) 22%,transparent)' }}>
              <Spec label="Property type" value={listing.property_type} />
              <Spec label="Tenure" value={tenure} />
              <Spec label="Built-up" value={sqft(listing.built_up_sqft)} />
              <Spec label="Land area" value={sqft(listing.land_area_sqft)} />
              <Spec label="Bedrooms" value={listing.bedrooms} />
              <Spec label="Bathrooms" value={listing.bathrooms} />
              <Spec label="Floor level" value={listing.floor_level} />
              <Spec label="Facing" value={listing.facing} />
            </div>
          )}

          {listing?.property_address && (
            <div style={{ paddingTop: hasSpecs ? 0 : 9, borderTop: hasSpecs ? 'none' : '1px solid color-mix(in srgb,var(--primary) 22%,transparent)' }}>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>Address</div>
              <div style={{ fontSize: 12.5, lineHeight: 1.4 }}>{listing.property_address}</div>
            </div>
          )}

          {!disabled && (
            <div style={{ display: 'flex', gap: 14 }}>
              <span onClick={() => setOpen(true)} style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--primary)', cursor: 'pointer' }}>Change</span>
              <span onClick={() => onChange?.(null)} style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--bad)', cursor: 'pointer' }}>Unlink</span>
            </div>
          )}
        </div>
      ) : (
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          onClick={() => !disabled && setOpen(true)}
          onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOpen(true); } }}
          style={{
            height: 'var(--field-h)', width: '100%', display: 'flex', alignItems: 'center', gap: 10,
            padding: '0 12px', borderRadius: 12, border: '1px solid var(--border)',
            background: disabled ? 'var(--surface2)' : 'var(--surface)', color: 'var(--muted)',
            fontSize: 14, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.65 : 1, userSelect: 'none',
          }}
        >
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ flex: 'none' }}>
            <circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="21" y2="21" />
          </svg>
          <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{placeholder}</span>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      )}

      <ListingPickerSheet
        open={open}
        onClose={() => setOpen(false)}
        listingType={listingType}
        status={status}
        linkable={linkable}
        value={value}
        title={title}
        sub={sub}
        onSelect={(l) => onChange?.(l)}
        onClear={linked ? () => onChange?.(null) : undefined}
      />
    </>
  );
}

// The Listings-tab card, plus what only matters when you're picking one to transact:
// whose listing it is, and what the standing offer is on an UNDER_OFFER property.
function ListingCard({ l, linked, onClick }) {
  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      style={{
        display: 'flex', gap: 14, padding: 12, background: 'var(--surface)',
        border: `1.5px solid ${linked ? 'var(--primary)' : 'var(--border)'}`,
        borderRadius: 14, cursor: 'pointer',
      }}
    >
      {l.cover_image_url ? (
        <img src={l.cover_image_url} alt="" style={{ width: 76, height: 76, borderRadius: 11, flex: 'none', objectFit: 'cover', border: '1px solid var(--border)' }} />
      ) : (
        <div style={{ width: 76, height: 76, borderRadius: 11, flex: 'none', background: 'repeating-linear-gradient(45deg,var(--surface2),var(--surface2) 6px,var(--chip) 6px,var(--chip) 12px)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontFamily: 'ui-monospace,monospace', fontSize: 10.5, color: 'var(--muted)' }}>photo</span>
        </div>
      )}

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
          <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, minWidth: 0 }}>{l.property_title || l.property_address}</div>
          <div style={{ display: 'flex', gap: 4, flex: 'none', alignItems: 'flex-start' }}>
            <Chip label={l.listing_type} tone={LISTING_TYPE_TONE[l.listing_type]} />
            {l.status === 'UNDER_OFFER' && (
              <Chip label="Under offer" tone={LISTING_STATUS_TONE.UNDER_OFFER} style={{ whiteSpace: 'normal', textAlign: 'center', lineHeight: 1.25, maxWidth: 62 }} />
            )}
          </div>
        </div>

        <div style={{ fontSize: 11.5, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <span style={{ fontFamily: 'ui-monospace,monospace' }}>{l.listing_ref}</span>
          {' · '}{[l.area, l.state].filter(Boolean).join(', ') || l.property_address || '—'}
          {l.property_type ? ` · ${l.property_type}` : ''}
        </div>

        {(l.bedrooms != null || l.bathrooms != null || l.built_up_sqft != null) && (
          <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>
            {[
              l.bedrooms != null ? `${l.bedrooms} bed` : null,
              l.bathrooms != null ? `${l.bathrooms} bath` : null,
              l.built_up_sqft != null ? `${Number(l.built_up_sqft).toLocaleString('en-US')} sqft` : null,
            ].filter(Boolean).join(' · ')}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{listingPriceLabel(l)}</span>
          {linked && <Chip label="✓ Linked" tone="ok" />}
        </div>

        <ListedByLine listing={l} />

        {l.status === 'UNDER_OFFER' && l.offer_amount_rm != null && (
          <div style={{ fontSize: 11.5, color: 'var(--warn)', fontWeight: 600 }}>Standing offer {RM(l.offer_amount_rm)}</div>
        )}
        {l.owner_name && (
          <div style={{ fontSize: 11.5, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {/* "Owner" alone got read as the listing's owner; this is the vendor. */}
            Property owner: {l.owner_name}{l.owner_phone ? ` · ${l.owner_phone}` : ''}
          </div>
        )}
      </div>
    </div>
  );
}
